import React, { useMemo } from 'react';
import _ from 'lodash';
import { useMediaQuery } from '@material-ui/core';
import { styled } from '@this/constants/themes';
import { withTheme } from 'styled-components';
import { media } from '@this/components/shared/atoms/media';
import {
  Header,
  Icon,
  Date,
  Remove,
  OutlineRailContent
} from '@this/components/reserve_trip/select/outline_item/outline_item.template';
import { Name } from '@this/src/components/reserve_trip/select/transport_box/transport_box_foreign.template';
import {
  BoxButtonArea,
  BoxButton
} from '@this/components/reserve_trip/select/transport_box/right/right_in_outline.template';
import { BoxButtonText } from '@this/components/reserve_trip/select/shared/box_parts';

import type NonOrderItem from '@this/domain/non_order_item2';
import type TransportElement from '@this/domain/transport_element2';
import type TransportElementTerminal from '@this/domain/transport_element_terminal/transport_element_terminal2';

interface Props {
  icon: number;
  item: NonOrderItem;
  element: TransportElement;
  editing: boolean;
  editable: boolean;
  deselectItemType?: (e: React.MouseEvent<HTMLElement>) => void;
  startEditingNonOrderItem?: (item: NonOrderItem) => void;
  toggleNonOrderItemNeed?: (item: NonOrderItem) => void;
  theme: { serviceName: string };
}

const getItemPriceTotal = (item: NonOrderItem) => item.price.totalPrice;

const getTransportTitle = (element: TransportElement) => element.title;

const getTerminalDateStr = (terminal?: TransportElementTerminal) => {
  if (!terminal) return '';
  return terminal.dateStr;
};

const getTerminalDateTimeStr = (terminal?: TransportElementTerminal) => {
  if (!terminal) return '';
  return terminal.time.clone().tz(terminal.timeZone).format('YYYY/MM/DD HH:mm');
};

const getBoardingStation = (element: TransportElement) => element.boardingStation || element.from?.name || '';

const getArrivalStation = (element: TransportElement) => element.arrivalStation || element.to?.name || '';

const TransportBlockTemplate: React.SFC<Props> = ({
  icon,
  item,
  element,
  editing,
  editable,
  deselectItemType,
  startEditingNonOrderItem,
  toggleNonOrderItemNeed,
  theme
}) => {
  const isMobile = useMediaQuery('(max-width:900px)');
  const styles = useMemo(() => getStyles(isMobile), [isMobile]);

  return (
    <Block className="select-outline__item-block" data-testid={`trips-form-transport-block-${item.id}`}>
      <Header $alignToRail $wrapActions>
        <Icon>{icon}</Icon>
        <Date data-testid={`trips-form-transport-block-date-${item.id}`}>{`交通機関：${getTerminalDateStr(
          element.from
        )}`}</Date>
        <div style={styles.headerActions}>
          {editable && item.isNeed && (
            <CheckLabel>
              <input type="checkbox" checked={false} disabled />
              システムで手配
            </CheckLabel>
          )}
          {editable && toggleNonOrderItemNeed && (
            <Remove style={styles.remove} onClick={() => toggleNonOrderItemNeed(item)}>
              {item.isNeed ? '削除する' : '元に戻す'}
            </Remove>
          )}
        </div>
      </Header>
      <OutlineRailContent>
        {item.isNeed && (
          <Card>
            <CardLeft>
              <Name data-testid={`trips-form-transport-block-name-${item.id}`}>{getTransportTitle(element)}</Name>
              <Text>
                {`発 ${getTerminalDateTimeStr(element.from)} ${getBoardingStation(element)}`}
                <br />
                {`着 ${getTerminalDateTimeStr(element.to)} ${getArrivalStation(element)}`}
              </Text>
            </CardLeft>
            <CardRight>
              <>
                <CardPrice data-testid={`trips-form-transport-block-price-${item.id}`}>
                  {utils.formatPriceWithMark(getItemPriceTotal(item))}
                </CardPrice>
                {editable && startEditingNonOrderItem && deselectItemType && (
                  <BlockButtonArea>
                    <BoxButton
                      selected={editing}
                      onClick={e => (editing ? deselectItemType(e) : startEditingNonOrderItem(item))}
                    >
                      <BoxButtonText className="select-transport-box__right-gray__button-text">
                        {editing ? (
                          <span>
                            編集を
                            <br />
                            キャンセル
                          </span>
                        ) : (
                          '編集 >'
                        )}
                      </BoxButtonText>
                    </BoxButton>
                  </BlockButtonArea>
                )}
              </>
            </CardRight>
          </Card>
        )}
      </OutlineRailContent>
    </Block>
  );
};

export const Block = styled.div`
  margin-bottom: 15px;

  ${media.sp`
    padding-left: 4px;
  `}
`;

export const CheckLabel = styled.label`
  color: ${props => props.theme.disabledColor};
  font-weight: inherit;
`;

export const Card = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  background: #fff;
  border: solid 2px ${props => props.theme.grayBorderColor};
`;

export const CardLeft = styled.div`
  padding: 10px;
`;

export const Text = styled.div`
  margin-top: 4px;
  font-size: 14px;
`;

export const CardRight = styled.div`
  display: flex;
  width: 100%;
  box-sizing: border-box;
  min-width: 170px;
  padding: 10px;
  background: ${props => props.theme.grayBgColorLight};

  ${media.sp`
    min-width: auto;
    padding: 16px 10px;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  `}
`;

export const CardPrice = styled.div`
  justify-content: center;
  margin: auto 0;
  color: ${props => props.theme.linkColor};
  font-size: 18px;
  font-weight: bold;

  ${media.sp`
    width: auto;
    margin: 0;
  `}
`;

export const BlockButtonArea = styled(BoxButtonArea)`
  color: ${props => props.theme.linkColor};
`;

const getStyles = (isMobile: boolean) => ({
  headerActions: isMobile
    ? {
        display: 'flex' as const,
        alignItems: 'center' as const,
        justifyContent: 'space-between' as const,
        width: '100%' as const,
        boxSizing: 'border-box' as const,
        paddingLeft: 40,
        marginTop: 6
      }
    : {
        display: 'flex' as const,
        alignItems: 'center' as const,
        justifyContent: 'space-between' as const
      },
  remove: isMobile ? ({ marginLeft: 0 } as const) : undefined
});

export default withTheme(TransportBlockTemplate);
